import { RenderBalanceInColor } from '@/components/features/accounts/account-utils'
import { DeleteMovementModal } from '@/components/features/accounts/DeleteMovementModal'
import { EditAccountModal } from '@/components/features/accounts/EditAccountModal'
import { MovementsTable } from '@/components/features/accounts/MovementsTable'
import DatePicker from '@/components/features/shared/DatePicker'
import { LoaderElement } from '@/components/features/shared/LoaderElement'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { OptionalLabel, PriceLabel } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PageContainer } from '@/components/features/shared/PageContainer'
import {
  Account,
  AccountStatus,
  formatAccountStatus,
  movementFormSchema,
  MovementFormValues
} from '@/domain/accounts/account'
import { MovementRequest } from '@/domain/accounts/expenses'
import { PaymentMethod } from '@/domain/payments/payments'
import { accountKeys, useAccount, useAccounts, useAddMovement, useDeleteMovement, useExportMovements, useMovementChart, useMovementCounts, useMovements } from '@/hooks/api/useAccounts'
import { useDateRange } from '@/hooks/useDateRange'
import { useServerPagination } from '@/hooks/useServerPagination'
import { AccountStatistics } from '@/pages/accounts/AccountStatistics'
import { Paths } from '@/routes/paths'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { format, startOfMonth } from 'date-fns'
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Download,
  Loader2, Pencil, Search, TrendingDown,
  Wallet, X
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

export const MainAccountDetails = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showExpenseForm, setShowExpenseForm] = useState(false)

  const params = useSearch({ strict: false }) as { accountId?: string }
  const accountId = params.accountId

  const { pagination, setPagination, updateQueryParams, inputValue: descriptionFilter } = useServerPagination({
    pageParam: 'page',
    limitParam: 'limit',
    searchParam: 'descriptionFilter',
    defaultLimit: 20,
    apiPageStart: 1,
  })

  const { fromDate, toDate, setFromDate, setToDate, dateFrom, dateTo } = useDateRange({ fromDate: startOfMonth(new Date()), toDate: new Date() })

  const addMovementMutation = useAddMovement()
  const deleteMovementMutation = useDeleteMovement()

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [pendingDeleteMovement, setPendingDeleteMovement] = useState<{ movementId: string; accountId: string } | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [accountStatusFilter, setAccountStatusFilter] = useState<AccountStatus | 'all'>(AccountStatus.ACTIVE)

  const { data: account, isLoading: isLoadingAccount } = useAccount(accountId, { enabled: !!accountId })
  const isCollectorView = location.href.includes(Paths.Management.CollectorAccounts)

  const { data: mainAccounts } = useAccounts(
    { limit: 1000, isMain: !isCollectorView, isCollector: isCollectorView, status: accountStatusFilter === 'all' ? undefined : accountStatusFilter},
  )

  const movementFilters = useMemo(() => ({
    accountId,
    startDate: fromDate,
    endDate: toDate,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    description: descriptionFilter || undefined,
  }), [accountId, fromDate, toDate, pagination.pageIndex, pagination.pageSize, descriptionFilter, account?.isMain, isCollectorView])

  const { data: paginatedMovements, isLoading: isLoadingMovements } = useMovements({
    accountId,
    filters: movementFilters
  })

  const { data: movementCounts } = useMovementCounts({
    accountId,
    filters: {
      accountId,
      startDate: movementFilters.startDate,
      endDate: movementFilters.endDate,
      description: movementFilters.description,
    }
  })

  const { data: chartData, isLoading: isLoadingChart } = useMovementChart({
    accountId,
    filters: {
      accountId,
      startDate: movementFilters.startDate,
      endDate: movementFilters.endDate,
      description: movementFilters.description,
    }
  })

  const movements = paginatedMovements?.data || []

  const today = format(new Date(), 'yyyy-MM-dd');

  const movementForm = useForm<MovementFormValues>({
    resolver: zodResolver(movementFormSchema),
    defaultValues: {
      amount: undefined,
      description: '',
      paymentMethod: PaymentMethod.CASH,
      observations: '',
      movementDate: today,
      paymentVerified: true,
      hasInvoice: false,
      invoiceNumber: '',
      taxRate: 21,
      subtotal: undefined,
      totalAmount: undefined,
    },
  });

  const selectedPaymentMethod = movementForm.watch('paymentMethod');
  const expenseAmount = movementForm.watch('amount');
  const hasInvoice = movementForm.watch('hasInvoice');
  const isCardPayment = [PaymentMethod.CREDIT_CARD, PaymentMethod.DEBIT_CARD].includes(selectedPaymentMethod);
  const isVerifiedPayment = !isCardPayment ? movementForm.watch('paymentVerified') : true;
  const hasExpenseErrors = !isVerifiedPayment;

  const taxRate = movementForm.watch('taxRate') ?? 21;
  const subtotal = movementForm.watch('subtotal');
  const totalAmount = movementForm.watch('totalAmount');

  const prevAmountRef = useRef(expenseAmount);
  useEffect(() => {
    if (!hasInvoice || !expenseAmount || expenseAmount <= 0) return;

    if (prevAmountRef.current === expenseAmount) return;
    prevAmountRef.current = expenseAmount;

    const calculatedTaxRate = taxRate || 21;
    const calculatedSubtotal = Math.round((expenseAmount / (1 + calculatedTaxRate / 100)) * 100) / 100;
    const calculatedTotal = Math.round(expenseAmount * 100) / 100;

    if (subtotal !== calculatedSubtotal) {
      movementForm.setValue('subtotal', calculatedSubtotal, { shouldValidate: true });
    } else if (totalAmount !== calculatedTotal) {
      movementForm.setValue('totalAmount', calculatedTotal, { shouldValidate: true });
    }
  }, [expenseAmount, hasInvoice, taxRate, movementForm]);

  const manualSubtotal = movementForm.watch('subtotal');
  const manualTotal = movementForm.watch('totalAmount');

  useEffect(() => {
    if (!hasInvoice || !expenseAmount || expenseAmount <= 0) return;

    if (manualSubtotal && manualSubtotal > 0) {
      const calculatedTotal = Math.round((manualSubtotal + manualSubtotal * (taxRate || 21) / 100) * 100) / 100;
      if (manualTotal !== calculatedTotal) {
        movementForm.setValue('totalAmount', calculatedTotal, { shouldValidate: true });
      }
    }
  }, [manualSubtotal, taxRate, hasInvoice, movementForm]);

  const handleExpenseSubmit = (values: MovementFormValues) => {
    if (expenseAmount! <= 0 || !accountId) return;
    const requestData: { accountId: string; data: MovementRequest } = {
      accountId,
      data: {
        amount: values.amount,
        description: values.description,
        paymentMethod: values.paymentMethod,
        observations: values.observations,
        movementDate: values.movementDate || today,
        invoiceNumber: values.hasInvoice ? values.invoiceNumber : undefined,
        taxRate: values.hasInvoice ? values.taxRate : undefined,
        subtotal: values.hasInvoice ? values.subtotal : undefined,
        totalAmount: values.hasInvoice ? values.totalAmount : undefined,
      },
    };
    addMovementMutation.mutate(requestData, {
      onSuccess: () => {
        setShowExpenseForm(false);
      },
    });
  }

  useEffect(() => {
    if (accountId) {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
    }
  }, [accountId]);

  const handleDeleteRequest = (movement: any) => {
    setPendingDeleteMovement({ movementId: movement.id, accountId: movement.accountId })
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (pendingDeleteMovement) {
      deleteMovementMutation.mutate(pendingDeleteMovement, {
        onSuccess: () => {
          setDeleteModalOpen(false)
          setPendingDeleteMovement(null)
        }
      })
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false)
    setPendingDeleteMovement(null)
  }

  const handleEditAccount = (account: Account) => {
    setEditingAccount(account)
    setEditModalOpen(true)
  }

  const handleEditCancel = () => {
    setEditModalOpen(false)
    setEditingAccount(null)
  }

  const exportMutation = useExportMovements()

  const handleExport = async () => {
    if (!account) return
    await exportMutation.mutateAsync({
      accountId: account.id,
      accountName: displayName(account).replaceAll('-', '').replaceAll(' ', '_'),
      filters: movementFilters,
    })
  }

  const selectAccount = (account: Account) => {
    if(account.isCollector){
      navigate({ to: `${Paths.Management.CollectorAccounts}`, search: { accountId: account.id, dateFrom, dateTo } })
    }
    else {
      navigate({ to: `${Paths.Management.MainAccounts}`, search: { accountId: account.id, dateFrom, dateTo } })
    }
  }

  const isLoading = isLoadingAccount

  if (isLoading) {
    return <LoaderElement />
  }

  const totalCredits = movementCounts?.totalCredits || 0
  const totalDebits = movementCounts?.totalDebits || 0
  const filteredBalance = movementCounts?.balance || 0

  const displayName = (account: Account) => {
    return `${account.accountNumber}${` - ${account.accountAlias}`}`
  }

  return (
    <PageContainer>
      <NavHeader>
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight">{isCollectorView ? 'Cuentas Cobradores' : 'Cuentas Principales'}</h1>
        </div>
      </NavHeader>

      {mainAccounts && mainAccounts.data.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold tracking-tight text-muted-foreground flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Cuentas Disponibles
            </h2>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Estado</Label>
              <Select value={accountStatusFilter} onValueChange={(v) => {
                setAccountStatusFilter(v as AccountStatus)
                navigate({ to: (isCollectorView ? Paths.Management.CollectorAccounts : Paths.Management.MainAccounts) as any })
              }}>
                <SelectTrigger className="w-[140px] h-8">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value={AccountStatus.ACTIVE}>Activas</SelectItem>
                  <SelectItem value={AccountStatus.SUSPENDED}>Suspendidas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <IconButton
              variant="primary"
              disabled={!accountId}
              onClick={() => {
                const selected = mainAccounts?.data.find(a => a.id === accountId)
                if (selected) handleEditAccount(selected)
              }}
            >
              <Pencil className="h-4 w-4 mr-1" />
              Editar Cuenta
            </IconButton>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {mainAccounts.data.map(acc => {
              const isSelected = acc.id === accountId
              return (
                <Card
                  key={acc.id}
                  className={`cursor-pointer transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20 bg-primary/[0.02]' : 'hover:border-primary/50 opacity-80 hover:opacity-100'} relative overflow-hidden`}
                  onClick={() => !isSelected && selectAccount(acc)}
                >
                  {isSelected && (
                    <div className="absolute top-0 right-0 p-1 bg-primary text-primary-foreground text-[10px] font-bold px-2 rounded-bl-lg">
                      Seleccionada
                    </div>
                  )}
                  <CardHeader className="px-4 py-3 flex flex-row items-center justify-between space-y-0">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm truncate" title={acc.accountAlias || `Cuenta ${acc.accountNumber}`}>
                        <span className="font-bold font-mono">{acc.accountNumber}</span>
                        <span className="font-medium capitalize wrap-text"> {acc.accountAlias.toLocaleLowerCase() || `Cuenta ${acc.accountNumber}`} </span>
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 pt-0 flex items-baseline justify-between">
                    <div className={`text-sm font-bold ${acc.currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <RenderBalanceInColor balance={acc.currentBalance} />
                    </div>
                    <Badge className='text-xs' variant={acc.status === AccountStatus.ACTIVE ? 'light' : 'destructive'}>
                      {formatAccountStatus(acc.status)}
                    </Badge>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </section>
      )}

      {showExpenseForm && account ? (
        <div className="container mx-auto pb-8 space-y-6">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold tracking-tight">Registrar Gasto</h1>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mt-6">
            <div className="xl:col-span-4 xl:order-last space-y-6">
              <Card className="h-full border-gray-200 shadow-sm flex flex-col overflow-hidden">
                <div className="bg-gray-900 text-white p-6">
                  <h2 className="text-lg font-semibold">Resumen</h2>
                </div>

                <CardContent className="p-0 flex-1 flex flex-col bg-white">
                  <div className="p-6 border-b border-gray-100">
                    <h3 className="text-xs font-bold text-gray-400 mb-3 uppercase tracking-wider">Cuenta Principal</h3>
                    <div className="space-y-1">
                      <p className="text-base font-semibold text-gray-900">{account.accountAlias || 'Principal'}</p>
                      <p className="text-sm text-gray-600 mt-1">Nro: {account.accountNumber}</p>
                      <p className="text-sm text-gray-500 mt-2">Saldo actual:</p>
                      <div className={`text-xl font-bold ${account.currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <PriceLabel price={account.currentBalance} />
                      </div>
                    </div>
                  </div>

                  <div className="p-6 bg-gray-50 mt-auto">
                    <div className="space-y-3">
                      {expenseAmount && expenseAmount > 0 && (
                        <>
                          <div className="flex justify-between items-center text-sm font-medium text-gray-600">
                            <span>Monto del gasto</span>
                            <PriceLabel price={expenseAmount} />
                          </div>
                          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                            <span className="text-base font-bold text-gray-900">Saldo futuro</span>
                            <div className={`text-xl font-bold ${account.currentBalance - expenseAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              <PriceLabel price={account.currentBalance - expenseAmount} />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="xl:col-span-8 xl:order-first">
              <Card className="h-full border-gray-200 shadow-sm">
                <CardHeader className="bg-white pb-4 border-b">
                  <CardTitle className="text-xl">Datos del gasto</CardTitle>
                  <CardDescription>Complete la información para registrar un gasto en la cuenta principal</CardDescription>
                </CardHeader>
                <CardContent className="pt-6 bg-white">
                  <Form {...movementForm}>
                    <form onSubmit={movementForm.handleSubmit(handleExpenseSubmit)} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-6">
                          <FormField
                            control={movementForm.control}
                            name="amount"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-700 font-semibold">Monto del gasto</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="Ingrese el monto"
                                    className="bg-white"
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={movementForm.control}
                            name="description"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-700 font-semibold">Concepto</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Descripción del gasto"
                                    className="bg-white"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={movementForm.control}
                            name="observations"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-700 font-semibold">Observaciones</FormLabel>
                                <OptionalLabel />
                                <FormControl>
                                  <Input
                                    placeholder=""
                                    className="bg-white"
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value)}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={movementForm.control}
                            name="movementDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-700 font-semibold">Fecha del gasto</FormLabel>
                                <FormControl>
                                  <DatePicker
                                    date={field.value ? (() => { const [y, m, d] = field.value.split('-').map(Number); return new Date(y, m - 1, d) })() : undefined}
                                    onSelect={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="space-y-6">
                          <FormField
                            control={movementForm.control}
                            name="paymentMethod"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-700 font-semibold">Método de pago</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger className="bg-white">
                                      <SelectValue placeholder="Seleccione método" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value={PaymentMethod.CREDIT_CARD}>Tarjeta de crédito</SelectItem>
                                    <SelectItem value={PaymentMethod.DEBIT_CARD}>Tarjeta de débito</SelectItem>
                                    <SelectItem value={PaymentMethod.BANK_TRANSFER}>Transferencia bancaria</SelectItem>
                                    <SelectItem value={PaymentMethod.CASH}>Efectivo</SelectItem>
                                    <SelectItem value={PaymentMethod.OTHER}>Otro</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {(selectedPaymentMethod === PaymentMethod.BANK_TRANSFER || selectedPaymentMethod === PaymentMethod.CASH || selectedPaymentMethod === PaymentMethod.OTHER) && (
                            <FormField
                              control={movementForm.control}
                              name="paymentVerified"
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-green-200 bg-green-50/50 p-4">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value}
                                      onCheckedChange={field.onChange}
                                      className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:text-white border-green-600"
                                    />
                                  </FormControl>
                                  <div className="flex flex-col gap-1">
                                    <FormLabel className="text-green-900 font-semibold">Confirmar procesamiento</FormLabel>
                                    <FormDescription className="text-xs text-green-700">
                                      Confirmo que el gasto ha sido procesado correctamente.
                                    </FormDescription>
                                  </div>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}

                          <FormField
                            control={movementForm.control}
                            name="hasInvoice"
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-blue-200 bg-blue-50/50 p-4">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    className="mt-1 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white border-blue-600"
                                  />
                                </FormControl>
                                <div className="flex flex-col gap-1">
                                  <FormLabel className="text-blue-900 font-semibold">¿Tiene factura?</FormLabel>
                                  <FormDescription className="text-xs text-blue-700">
                                    Habilite para completar los campos de facturación.
                                  </FormDescription>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      {movementForm.getValues('hasInvoice') && (
                        <div className="col-span-1 md:col-span-2 pt-4 border-t border-gray-200">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <FormField
                              control={movementForm.control}
                              name="invoiceNumber"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700 font-semibold">N° de Factura</FormLabel>
                                  <FormControl>
                                    <Input
                                      //  placeholder="Ej: A-0001234"
                                      className="bg-white"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={movementForm.control}
                              name="taxRate"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700 font-semibold">Tasa impositiva (%)</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="21"
                                      className="bg-white"
                                      {...field}
                                      value={field.value ?? 21}
                                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 21)}
                                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={movementForm.control}
                              name="subtotal"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700 font-semibold">Subtotal</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="0.00"
                                      className="bg-white"
                                      {...field}
                                      value={field.value ?? ''}
                                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={movementForm.control}
                              name="totalAmount"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700 font-semibold">Total</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="0.00"
                                      className="bg-white"
                                      {...field}
                                      value={field.value ?? ''}
                                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                      )}

                      {hasExpenseErrors && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
                          <AlertCircle className="mr-2 h-4 w-4 text-red-600" />
                          <span className="text-sm font-medium text-red-800">
                            No puede registrar gastos con métodos que requieren confirmación sin verificar
                          </span>
                        </div>
                      )}

                      <div className="pt-4 border-t flex flex-col sm:flex-row justify-between gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="lg"
                          onClick={() => setShowExpenseForm(false)}
                          disabled={addMovementMutation.isPending}
                        >
                          <ArrowLeft className="mr-2 h-4 w-4" />
                          Cancelar
                        </Button>
                        <Button
                          type="submit"
                          size="lg"
                          variant="info"
                          className="w-full sm:w-auto font-semibold px-8 shadow-sm"
                          disabled={addMovementMutation.isPending || hasExpenseErrors}
                        >
                          {addMovementMutation.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          {!addMovementMutation.isPending && (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              <span>Registrar gasto</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        <div className="pt-2 space-y-4">
          <Tabs defaultValue="movements">
            <div className="flex items-center justify-between">
              <TabsList className="grid w-full max-w-[400px] grid-cols-2">
                <TabsTrigger value="movements">Movimientos</TabsTrigger>
                <TabsTrigger value="statistics">Estadísticas</TabsTrigger>
              </TabsList>
              {account?.isMain && !isCollectorView && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 ml-4 shrink-0"
                  onClick={() => setShowExpenseForm(true)}
                  disabled={addMovementMutation.isPending}
                >
                  <TrendingDown className="mr-2 h-4 w-4" />
                  Registrar Gasto
                </Button>
              )}
            </div>

            <TabsContent value="movements" className="space-y-4 mt-6">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Últimos Movimientos</h2>
                  <p className="text-muted-foreground">Registro de actividad reciente en la cuenta {account?.accountNumber}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-[10px] uppercase text-muted-foreground font-bold">Saldo en Período seleccionado</p>
                  <p className={`text-xl font-bold tracking-tight`}>
                    <RenderBalanceInColor balance={filteredBalance} />
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="relative w-full sm:w-64">
                  <Label htmlFor="descriptionFilter" className="text-[10px] uppercase text-muted-foreground font-bold">Buscar concepto</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="descriptionFilter"
                      type="text"
                      placeholder="Filtrar por concepto..."
                      value={descriptionFilter}
                      onChange={(e) => {
                        updateQueryParams({ descriptionFilter: e.target.value || undefined, page: 1 })
                      }}
                      className="pl-8 h-8"
                    />
                    {descriptionFilter && (
                      <button
                        type="button"
                        onClick={() => updateQueryParams({ descriptionFilter: undefined, page: 1 })}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="grid gap-1">
                    <Label htmlFor="dateFrom" className="text-[10px] uppercase text-muted-foreground font-bold">Desde</Label>
                    <DatePicker
                      date={fromDate}
                      onSelect={(date) => {
                        setFromDate(date)
                        updateQueryParams({ page: 1 })
                      }}
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="dateTo" className="text-[10px] uppercase text-muted-foreground font-bold">Hasta</Label>
                    <DatePicker
                      date={toDate}
                      onSelect={(date) => {
                        setToDate(date)
                        updateQueryParams({ page: 1 })
                      }}
                    />
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 self-end"
                  onClick={handleExport}
                  disabled={exportMutation.isPending}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportar CSV
                </Button>
              </div>

              <Card className="w-full">
                <CardContent className="">
                  {isLoadingMovements ? (
                    <div className="space-y-3">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                      ))}
                    </div>
                  ) : (
                    <MovementsTable
                      paginated={paginatedMovements}
                      pagination={pagination}
                      onPaginationChange={setPagination}
                      onDeleteRequest={handleDeleteRequest}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="statistics" className="space-y-4 mt-6">
              {isLoadingChart ? <LoaderElement /> : <AccountStatistics totalCredits={totalCredits} totalDebits={totalDebits} chartData={chartData || []} dateFrom={dateFrom} dateTo={dateTo} />}
            </TabsContent>
          </Tabs>
        </div>
      )}
      <DeleteMovementModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        movementDescription={pendingDeleteMovement ? movements?.find(m => m.id === pendingDeleteMovement.movementId)?.description : undefined}
      />
      <EditAccountModal
        isOpen={editModalOpen}
        onClose={handleEditCancel}
        account={editingAccount}
      />
    </PageContainer>
  )
}